import React, { useState } from "react";
import CONFIG from '../../config';

function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({
    type: "",
    message: "",
  });

  const supportInfo = {
    customerSupport: {
      title: "Customer Support",
      description:
        "For service booking help, customer issues, payment support, or general platform queries.",
      email: "customer_support@digilaboursolutions.com",
      phone: "+91-8080808080",
    },
    labourSupport: {
      title: "Labour Registration",
      description:
        "For worker onboarding, profile registration, labour login help, and job-related assistance.",
      email: "hr@digilaboursolutions.com",
      phone: "+91-9090909080",
    },
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContact = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const response = await fetch(
		`${CONFIG.API_BASE_URL}/api/query/contact_us/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Something went wrong. Please try again.");
      }

      setStatus({
        type: "success",
        message: data?.message || "Your message has been sent successfully.",
      });

      setFormData({
        name: "",
        mobile: "",
        email: "",
        message: "",
      });
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message || "Unable to send your message right now.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const styles = {
    page: {
      minHeight: "100vh",
      background: "linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%)",
      padding: "40px 16px",
      fontFamily: '"Poppins", "Segoe UI", sans-serif',
    },

    container: {
      maxWidth: "1180px",
      margin: "0 auto",
    },

    hero: {
      textAlign: "center",
      marginBottom: "28px",
    },

    badge: {
      display: "inline-block",
      padding: "8px 14px",
      borderRadius: "999px",
      backgroundColor: "#e8f0ff",
      color: "#0d6efd",
      fontSize: "13px",
      fontWeight: 700,
      marginBottom: "12px",
    },

    title: {
      margin: "0 0 10px 0",
      fontSize: "32px",
      fontWeight: 800,
      color: "#0f172a",
      lineHeight: 1.2,
    },

    subtitle: {
      margin: "0 auto",
      maxWidth: "760px",
      fontSize: "15px",
      lineHeight: 1.7,
      color: "#475569",
    },

    layout: {
      display: "grid",
      gridTemplateColumns: "1.2fr 0.8fr",
      gap: "24px",
      alignItems: "start",
    },

    leftCard: {
      backgroundColor: "#ffffff",
      borderRadius: "22px",
      padding: "28px",
      boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
      border: "1px solid rgba(15, 23, 42, 0.06)",
    },

    rightWrap: {
      display: "flex",
      flexDirection: "column",
      gap: "20px",
    },

    infoCard: {
      backgroundColor: "#ffffff",
      borderRadius: "22px",
      padding: "22px",
      boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
      border: "1px solid rgba(15, 23, 42, 0.06)",
    },

    sectionTitle: {
      margin: "0 0 8px 0",
      fontSize: "22px",
      fontWeight: 700,
      color: "#0f172a",
    },

    sectionText: {
      margin: "0 0 22px 0",
      fontSize: "14px",
      color: "#64748b",
      lineHeight: 1.7,
    },

    formGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "16px",
      marginBottom: "16px",
    },

    inputGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      marginBottom: "16px",
    },

    label: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#1e293b",
    },

    input: {
      width: "100%",
      border: "1px solid #dbe4f0",
      borderRadius: "14px",
      padding: "14px 16px",
      fontSize: "14px",
      outline: "none",
      backgroundColor: "#f8fbff",
      color: "#0f172a",
    },

    textarea: {
      width: "100%",
      minHeight: "140px",
      border: "1px solid #dbe4f0",
      borderRadius: "14px",
      padding: "14px 16px",
      fontSize: "14px",
      outline: "none",
      resize: "vertical",
      backgroundColor: "#f8fbff",
      color: "#0f172a",
    },

    submitButton: {
      width: "100%",
      border: "none",
      borderRadius: "14px",
      padding: "14px 18px",
      background: isSubmitting
        ? "#94a3b8"
        : "linear-gradient(135deg, #0d6efd, #003c99)",
      color: "#ffffff",
      fontSize: "15px",
      fontWeight: 700,
      cursor: isSubmitting ? "not-allowed" : "pointer",
      boxShadow: isSubmitting
        ? "none"
        : "0 12px 28px rgba(13, 110, 253, 0.22)",
    },

    statusBox: {
      marginTop: "16px",
      borderRadius: "12px",
      padding: "12px 14px",
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: 1.6,
      backgroundColor:
        status.type === "success" ? "#ecfdf3" : status.type === "error" ? "#fef2f2" : "transparent",
      color:
        status.type === "success" ? "#166534" : status.type === "error" ? "#b91c1c" : "#475569",
      border:
        status.type === "success"
          ? "1px solid #bbf7d0"
          : status.type === "error"
          ? "1px solid #fecaca"
          : "none",
    },

    infoHeader: {
      margin: "0 0 14px 0",
      fontSize: "20px",
      fontWeight: 700,
      color: "#0f172a",
    },

    infoDescription: {
      margin: "0 0 18px 0",
      fontSize: "14px",
      lineHeight: 1.7,
      color: "#64748b",
    },

    contactItem: {
      marginBottom: "12px",
      fontSize: "14px",
      lineHeight: 1.7,
      color: "#334155",
      wordBreak: "break-word",
    },

    contactLabel: {
      fontWeight: 700,
      color: "#0f172a",
      marginRight: "6px",
    },

    link: {
      color: "#0d6efd",
      textDecoration: "none",
      fontWeight: 600,
    },

    quickHelpCard: {
      background: "linear-gradient(135deg, #0f172a, #1e3a8a)",
      color: "#ffffff",
      borderRadius: "22px",
      padding: "22px",
      boxShadow: "0 18px 45px rgba(15, 23, 42, 0.14)",
    },

    quickHelpTitle: {
      margin: "0 0 10px 0",
      fontSize: "20px",
      fontWeight: 700,
    },

    quickHelpText: {
      margin: "0 0 16px 0",
      fontSize: "14px",
      lineHeight: 1.7,
      color: "rgba(255,255,255,0.84)",
    },

    quickHelpList: {
      paddingLeft: "18px",
      margin: 0,
      fontSize: "14px",
      lineHeight: 1.8,
      color: "#ffffff",
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.hero}>
          <span style={styles.badge}>Get in Touch</span>
          <h1 style={styles.title}>Contact Us</h1>
          <p style={styles.subtitle}>
            Have a service query, booking issue, labour registration question, or
            general support request? Send us your message and our team will get
            back to you.
          </p>
        </div>

        <div
          style={{
            ...styles.layout,
            gridTemplateColumns:
              typeof window !== "undefined" && window.innerWidth <= 900
                ? "1fr"
                : "1.2fr 0.8fr",
          }}
        >
          <div style={styles.leftCard}>
            <h2 style={styles.sectionTitle}>Send Your Query</h2>
            <p style={styles.sectionText}>
              Fill out the form below for customer support, booking support, or
              general enquiries.
            </p>

            <form onSubmit={handleContact}>
              <div style={styles.formGrid}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                    style={styles.input}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Mobile Number</label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter your mobile number"
                    required
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  required
                  style={styles.input}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Write your message here"
                  required
                  style={styles.textarea}
                />
              </div>

              <button type="submit" disabled={isSubmitting} style={styles.submitButton}>
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>

              {status.message && <div style={styles.statusBox}>{status.message}</div>}
            </form>
          </div>

          <div style={styles.rightWrap}>
            <div style={styles.infoCard}>
              <h3 style={styles.infoHeader}>{supportInfo.customerSupport.title}</h3>
              <p style={styles.infoDescription}>
                {supportInfo.customerSupport.description}
              </p>
              <p style={styles.contactItem}>
                <span style={styles.contactLabel}>Email:</span>
                <a
                  href={`mailto:${supportInfo.customerSupport.email}`}
                  style={styles.link}
                >
                  {supportInfo.customerSupport.email}
                </a>
              </p>
              <p style={styles.contactItem}>
                <span style={styles.contactLabel}>Phone:</span>
                <a
                  href={`tel:${supportInfo.customerSupport.phone.replace(/\s+/g, "")}`}
                  style={styles.link}
                >
                  {supportInfo.customerSupport.phone}
                </a>
              </p>
            </div>

            <div style={styles.infoCard}>
              <h3 style={styles.infoHeader}>{supportInfo.labourSupport.title}</h3>
              <p style={styles.infoDescription}>
                {supportInfo.labourSupport.description}
              </p>
              <p style={styles.contactItem}>
                <span style={styles.contactLabel}>Email:</span>
                <a
                  href={`mailto:${supportInfo.labourSupport.email}`}
                  style={styles.link}
                >
                  {supportInfo.labourSupport.email}
                </a>
              </p>
              <p style={styles.contactItem}>
                <span style={styles.contactLabel}>Phone:</span>
                <a
                  href={`tel:${supportInfo.labourSupport.phone.replace(/\s+/g, "")}`}
                  style={styles.link}
                >
                  {supportInfo.labourSupport.phone}
                </a>
              </p>
            </div>

            <div style={styles.quickHelpCard}>
              <h3 style={styles.quickHelpTitle}>Quick Help</h3>
              <p style={styles.quickHelpText}>
                Use the contact form for detailed queries, document support, or
                service-related requests.
              </p>
              <ul style={styles.quickHelpList}>
                <li>Customer booking assistance</li>
                <li>Service complaints and follow-up</li>
                <li>Labour onboarding support</li>
                <li>Registration and profile help</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactUs;